import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ServiceCatalogService } from '../../core/services/service-catalog.service';
import { InstitutionService } from '../../core/services/institution.service';
import { ToastService } from '../../core/services/toast.service';
import { AuthService } from '../../core/services/auth.service';
import { ServiceCatalogResponse, UpdateServiceRequest, InstitutionResponse, ServiceRequiredAttachmentDto, AttachmentTypeDto, ServiceDocumentationAttachmentDto } from '../../core/models/models';
import { AttachmentTypeService } from '../../core/services/attachment-type.service';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { ModalComponent, ModalErrorState } from '../../shared/components/modal/modal.component';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { extractErrorDetails } from '../../core/interceptors/response.interceptor';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';

@Component({
  selector: 'app-service-catalog',
  standalone: true,
  imports: [CommonModule, FormsModule, StatusBadgeComponent, ModalComponent, PaginationComponent],
  template: `
    <div class="space-y-6 font-sans">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight">Government Service Catalog</h1>
          <p class="text-xs text-slate-500">Directory of published e-Government API services, data feeds, and inter-agency integration endpoints.</p>
        </div>

        <div class="flex flex-wrap items-center gap-2">
          @if (authService.isInternal() || authService.hasRole('SuperAdmin')) {
            <select
              [(ngModel)]="selectedSyncInstitutionUid"
              class="py-2.5 px-3 rounded-xl border border-slate-300 bg-white text-xs font-semibold text-slate-800 focus:outline-none focus:border-emerald-500 shadow-2xs"
            >
              <option value="">-- Choose Institution to Sync --</option>
              @for (inst of institutions(); track inst.uid) {
                <option [value]="inst.uid">{{ inst.name }} ({{ inst.memberCode || 'No Code' }})</option>
              }
            </select>
          }

          @if (authService.hasPermission('can sync services') || authService.hasPermission('can manage services') || authService.hasRole('SuperAdmin') || authService.hasInstitution()) {
            <button
              (click)="onSyncServices()"
              [disabled]="isSyncing() || ((authService.isInternal() || authService.hasRole('SuperAdmin')) && !selectedSyncInstitutionUid)"
              class="inline-flex items-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all active:scale-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg class="w-4 h-4" [class.animate-spin]="isSyncing()" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{{ isSyncing() ? 'Syncing Security Server Services...' : 'Sync Services from Central Server' }}</span>
            </button>
          }
        </div>
      </div>

      <!-- Search, Status Tabs & Filters -->
      <div class="glass-panel p-4 rounded-2xl border border-slate-200 bg-white flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-sm">
        <!-- Status Tabs -->
        <div class="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
          <button
            (click)="onStatusTabChange('ALL')"
            [ngClass]="statusTab() === 'ALL' ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold shadow-2xs' : 'text-slate-500 hover:text-slate-900 border-transparent'"
            class="px-3.5 py-1.5 rounded-xl text-xs whitespace-nowrap border transition-all cursor-pointer"
          >
            All Services ({{ services().length }})
          </button>
          @if (authService.hasInstitution()) {
            <button
              (click)="onStatusTabChange('MY_INSTITUTION')"
              [ngClass]="statusTab() === 'MY_INSTITUTION' ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold shadow-2xs' : 'text-slate-500 hover:text-slate-900 border-transparent'"
              class="px-3.5 py-1.5 rounded-xl text-xs whitespace-nowrap border transition-all cursor-pointer inline-flex items-center gap-1.5"
            >
              <span>My Institution</span>
              <span class="px-1.5 py-0.2 rounded-full text-[10px] bg-emerald-200/80 text-emerald-900 font-mono font-bold">{{ myServicesCount() }}</span>
            </button>
          }
          <button
            (click)="onStatusTabChange('ACTIVE')"
            [ngClass]="statusTab() === 'ACTIVE' ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold shadow-2xs' : 'text-slate-500 hover:text-slate-900 border-transparent'"
            class="px-3.5 py-1.5 rounded-xl text-xs whitespace-nowrap border transition-all cursor-pointer"
          >
            Active
          </button>
          <button
            (click)="onStatusTabChange('PENDING')"
            [ngClass]="statusTab() === 'PENDING' ? 'bg-emerald-50 text-emerald-800 border-emerald-300 font-bold shadow-2xs' : 'text-slate-500 hover:text-slate-900 border-transparent'"
            class="px-3.5 py-1.5 rounded-xl text-xs whitespace-nowrap border transition-all cursor-pointer"
          >
            Pending Approval
          </button>
        </div>

        <div class="flex items-center gap-3">
          <input
            type="text"
            [(ngModel)]="searchQuery"
            (ngModelChange)="currentPage.set(1)"
            placeholder="Search by name, code or institution..."
            class="px-3.5 py-2 rounded-xl bg-white border border-slate-300 text-xs text-slate-900 placeholder-slate-400 w-full md:w-64 focus:border-emerald-500 focus:outline-none"
          />

          <div class="flex items-center gap-1.5 border border-slate-200 p-1 rounded-xl bg-slate-50 shrink-0">
            <button
              (click)="viewMode.set('table')"
              title="Table View"
              class="p-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
              [ngClass]="viewMode() === 'table' ? 'bg-white text-emerald-800 shadow-2xs' : 'text-slate-500 hover:text-slate-800'"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
            <button
              (click)="viewMode.set('grid')"
              title="Grid View"
              class="p-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
              [ngClass]="viewMode() === 'grid' ? 'bg-white text-emerald-800 shadow-2xs' : 'text-slate-500 hover:text-slate-800'"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Table View -->
      @if (viewMode() === 'table') {
        <div class="glass-panel rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div class="overflow-x-auto">
            <table class="w-full text-left border-collapse text-xs">
              <thead>
                <tr class="bg-slate-100/80 text-slate-600 uppercase tracking-wider font-bold border-b border-slate-200">
                  <th class="p-4">Service Code</th>
                  <th class="p-4">Service Name</th>
                  <th class="p-4">Encoded ID</th>
                  <th class="p-4">Publishing Institution</th>
                  <th class="p-4">Approved By</th>
                  <th class="p-4">Status</th>
                  <th class="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-200/80">
                @for (srv of pagedServices(); track srv.uid) {
                  <tr class="hover:bg-emerald-50/30 transition-colors">
                    <td class="p-4 font-mono font-bold text-emerald-700">{{ srv.code }}</td>
                    <td class="p-4">
                      <div class="flex items-center gap-2">
                        <span class="font-bold text-slate-900">{{ srv.name }}</span>
                        @if (srv.requiredAttachments && srv.requiredAttachments.length > 0) {
                          <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200" [title]="srv.requiredAttachments.length + ' required compliance documents'">
                            <svg class="w-3 h-3 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            {{ srv.requiredAttachments.length }} Doc{{ srv.requiredAttachments.length > 1 ? 's' : '' }}
                          </span>
                        }
                        @if (srv.documentationAttachments && srv.documentationAttachments.length > 0) {
                          <button
                            type="button"
                            (click)="openServiceDocsModal(srv)"
                            class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition cursor-pointer"
                            title="Preview attached specifications and OpenAPI docs"
                          >
                            <svg class="w-3 h-3 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                            </svg>
                            {{ srv.documentationAttachments.length }} Spec{{ srv.documentationAttachments.length > 1 ? 's' : '' }} & Doc{{ srv.documentationAttachments.length > 1 ? 's' : '' }}
                          </button>
                        }
                      </div>
                      @if (srv.description) {
                        <div class="text-[11px] text-slate-500 line-clamp-1 mt-0.5">{{ srv.description }}</div>
                      }
                    </td>
                    <td class="p-4 font-mono text-[11px] text-slate-600">
                      @if (srv.encodedId) {
                        <span class="bg-slate-100 px-2 py-0.5 rounded border border-slate-200 truncate block max-w-[200px]" [title]="srv.encodedId">{{ srv.encodedId }}</span>
                      } @else {
                        <span class="text-slate-400 italic">-</span>
                      }
                    </td>
                    <td class="p-4 text-slate-700 font-medium">
                      <div class="flex items-center gap-1.5 flex-wrap">
                        <span>{{ srv.institutionName }}</span>
                        @if (isOwnInstitutionService(srv)) {
                          <span class="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0">
                            Your Institution
                          </span>
                        }
                      </div>
                    </td>
                    <td class="p-4 text-slate-500 font-mono">{{ srv.approvedByName || 'Pending' }}</td>
                    <td class="p-4">
                      <app-status-badge [status]="srv.active ? 'ACTIVE' : 'PENDING'"></app-status-badge>
                    </td>
                    <td class="p-4 text-right space-x-1.5 whitespace-nowrap">
                      <!-- Activate / Approve Action (for inactive items by Institution Admin or SuperAdmin/regulator) -->
                      @if (canActivate(srv)) {
                        <button
                          (click)="onActivateService(srv.uid)"
                          title="Activate Service"
                          class="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs transition border border-emerald-600 shadow-2xs cursor-pointer inline-flex items-center gap-1"
                        >
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                          </svg>
                          Activate
                        </button>
                      }

                      <!-- Edit / Update Action (only for own institution or SuperAdmin) -->
                      @if (canManageService(srv)) {
                        <button
                          (click)="openEditModal(srv)"
                          title="Edit Service Details"
                          class="p-1.5 rounded-lg bg-sky-50 hover:bg-sky-600 text-sky-700 hover:text-white transition border border-sky-200 shadow-2xs inline-flex items-center cursor-pointer"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      }

                      <!-- Delete Action (only for own institution or SuperAdmin) -->
                      @if (canManageService(srv)) {
                        <button
                          (click)="confirmDelete(srv)"
                          title="Delete Service"
                          class="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white transition border border-rose-200 shadow-2xs inline-flex items-center cursor-pointer"
                        >
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      }

                      <!-- Request Data Sharing Action (for external active services) -->
                      @if (!canManageService(srv) && !canApprove(srv) && srv.active) {
                        <button
                          type="button"
                          (click)="requestDataSharing(srv)"
                          title="Initiate Data Sharing Request (Consent & Mandatory Documents)"
                          class="px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs transition shadow-2xs cursor-pointer inline-flex items-center gap-1"
                        >
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          Request Access
                        </button>
                      } @else if (!canManageService(srv) && !canApprove(srv)) {
                        <span class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium text-slate-400 bg-slate-50 border border-slate-200/80">
                          <svg class="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                          </svg>
                          Read-Only
                        </span>
                      }
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="6" class="p-8 text-center text-slate-500">
                      No government services found matching your current filter criteria.
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <app-pagination
            [currentPage]="currentPage()"
            [pageSize]="pageSize()"
            [totalItems]="totalItems()"
            (pageChange)="onPageChange($event)"
            (pageSizeChange)="onPageSizeChange($event)"
          ></app-pagination>
        </div>
      } @else {
        <!-- Grid View -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          @for (srv of pagedServices(); track srv.uid) {
            <div class="glass-card p-6 rounded-2xl border border-slate-200 bg-white space-y-4 flex flex-col justify-between relative group shadow-sm hover:border-emerald-400 hover:shadow-md transition-all">
              <div class="space-y-2">
                <div class="flex items-center justify-between">
                  <span class="px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                    {{ srv.code }}
                  </span>
                  <app-status-badge [status]="srv.active ? 'ACTIVE' : 'PENDING'"></app-status-badge>
                </div>

                <div class="flex items-start justify-between gap-2">
                  <h3 class="text-base font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">
                    {{ srv.name }}
                  </h3>
                  @if (srv.requiredAttachments && srv.requiredAttachments.length > 0) {
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 shrink-0" [title]="srv.requiredAttachments.length + ' required compliance documents'">
                      <svg class="w-3 h-3 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {{ srv.requiredAttachments.length }} Docs
                    </span>
                  }
                </div>

                @if (srv.encodedId) {
                  <div class="text-[11px] font-mono text-slate-600 bg-slate-50 p-2 rounded-xl border border-slate-200/80 truncate" [title]="srv.encodedId">
                    <span class="text-[9px] uppercase font-bold text-slate-400 block tracking-wider">X-Road Encoded ID</span>
                    {{ srv.encodedId }}
                  </div>
                }

                <p class="text-xs text-slate-600 line-clamp-3 leading-relaxed">
                  {{ srv.description || 'No endpoint description provided.' }}
                </p>

                @if (srv.documentationAttachments && srv.documentationAttachments.length > 0) {
                  <button
                    type="button"
                    (click)="openServiceDocsModal(srv)"
                    class="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-[11px] font-bold bg-teal-50 text-teal-800 border border-teal-200 hover:bg-teal-100 transition cursor-pointer shadow-2xs"
                  >
                    <svg class="w-3.5 h-3.5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                    <span>{{ srv.documentationAttachments.length }} Specs & Docs Available</span>
                  </button>
                }
              </div>

              <div class="pt-4 border-t border-slate-100 flex items-center justify-between text-xs gap-2">
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5">
                    <span class="text-[10px] text-slate-400 uppercase block font-bold">Publishing Body</span>
                    @if (isOwnInstitutionService(srv)) {
                      <span class="px-1.5 py-0.2 rounded text-[9px] font-bold bg-emerald-100 text-emerald-800">Yours</span>
                    }
                  </div>
                  <span class="font-semibold text-slate-800 truncate block">{{ srv.institutionName }}</span>
                </div>

                <div class="flex items-center gap-1.5 shrink-0">
                  @if (canActivate(srv)) {
                    <button
                      (click)="onActivateService(srv.uid)"
                      title="Activate Service"
                      class="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-2xs cursor-pointer inline-flex items-center gap-1"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Activate
                    </button>
                  }

                  @if (canManageService(srv)) {
                    <button
                      (click)="openEditModal(srv)"
                      title="Edit Service"
                      class="p-1.5 rounded-lg bg-sky-50 hover:bg-sky-600 text-sky-700 hover:text-white transition border border-sky-200 cursor-pointer"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>

                    <button
                      (click)="confirmDelete(srv)"
                      title="Delete Service"
                      class="p-1.5 rounded-lg bg-rose-50 hover:bg-rose-600 text-rose-700 hover:text-white transition border border-rose-200 cursor-pointer"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  }

                  @if (!canManageService(srv) && !canApprove(srv) && srv.active) {
                    <button
                      type="button"
                      (click)="requestDataSharing(srv)"
                      title="Initiate Data Sharing Request (Consent & Mandatory Documents)"
                      class="px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs transition shadow-2xs cursor-pointer inline-flex items-center gap-1"
                    >
                      <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      Request Access
                    </button>
                  } @else if (!canManageService(srv) && !canApprove(srv)) {
                    <span class="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-slate-400 bg-slate-50 border border-slate-200/80">
                      <svg class="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                      </svg>
                      Read-Only
                    </span>
                  }
                </div>
              </div>
            </div>
          }
        </div>

        @if (totalItems() > 0) {
          <div class="glass-panel rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-xs">
            <app-pagination
              [currentPage]="currentPage()"
              [pageSize]="pageSize()"
              [totalItems]="totalItems()"
              (pageChange)="onPageChange($event)"
              (pageSizeChange)="onPageSizeChange($event)"
            ></app-pagination>
          </div>
        }
      }

      <!-- Edit / Update Service Modal -->
      <app-modal
        [isOpen]="showEditModal()"
        title="Edit Service Details"
        maxWidth="max-w-4xl"
        [errorState]="modalError()"
        (close)="showEditModal.set(false)"
      >
        <form (ngSubmit)="onUpdateService()" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <!-- Col 1: Service Display Name -->
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-bold text-slate-700 uppercase">Service Display Name</label>
                <span class="text-[10px] text-slate-400 font-medium italic">Central Server</span>
              </div>
              <input
                type="text"
                [value]="selectedService()?.name || ''"
                disabled
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-700 text-xs font-semibold cursor-not-allowed"
              />
            </div>

            <!-- Col 2: Service Code -->
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-bold text-slate-700 uppercase">Service Code</label>
                <span class="text-[10px] text-slate-400 font-medium italic">Subsystem</span>
              </div>
              <input
                type="text"
                [value]="selectedService()?.code || ''"
                disabled
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-700 text-xs font-mono font-bold cursor-not-allowed"
              />
            </div>

            <!-- Col 3: Encoded ID -->
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-bold text-slate-700 uppercase">Encoded ID</label>
                <span class="text-[10px] text-slate-400 font-medium italic">X-Road ID</span>
              </div>
              <input
                type="text"
                [value]="selectedService()?.encodedId || 'N/A'"
                disabled
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-600 text-xs font-mono cursor-not-allowed truncate"
              />
            </div>

            <!-- Row 2, Col 1: Publishing Institution -->
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Publishing Institution</label>
              <input
                type="text"
                [value]="selectedService()?.institutionName || ''"
                disabled
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-100 text-slate-600 text-xs font-semibold cursor-not-allowed"
              />
            </div>

            <!-- Row 2, Col 2: Service Status -->
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Service Status</label>
              <select
                [(ngModel)]="editForm.active"
                name="editActive"
                class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs font-medium focus:border-emerald-500 focus:outline-none cursor-pointer"
              >
                <option [ngValue]="true">ACTIVE (Published & Enforced)</option>
                <option [ngValue]="false">PENDING / INACTIVE (Suspended)</option>
              </select>
            </div>

            <!-- Row 2, Col 3: Technical Endpoint Description -->
            <div>
              <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Technical Endpoint Description</label>
              <textarea
                [(ngModel)]="editForm.description"
                name="editDescription"
                rows="1"
                placeholder="Add technical notes, documentation links, API schema summaries..."
                class="w-full px-3.5 py-2 rounded-xl border border-slate-300 bg-white text-slate-900 text-xs focus:border-emerald-500 focus:outline-none resize-y"
              ></textarea>
            </div>
          </div>

          <!-- Required Compliance Attachments Builder -->
          <div class="pt-4 border-t border-slate-200 space-y-3">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <div class="flex items-center gap-2">
                  <label class="block text-xs font-extrabold text-slate-800 uppercase tracking-tight">Required Compliance Documents</label>
                  <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                    {{ editForm.requiredAttachments.length }} Configured
                  </span>
                </div>
                <p class="text-[11px] text-slate-500 mt-0.5">Define mandatory or optional attachments requesting consumers must upload when applying for DSR.</p>
              </div>
              <button
                type="button"
                (click)="showAddAttachmentForm.set(!showAddAttachmentForm())"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 transition cursor-pointer self-start sm:self-auto shadow-2xs"
              >
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
                <span>{{ showAddAttachmentForm() ? 'Close Custom Form' : 'Add Custom Document' }}</span>
              </button>
            </div>

            <!-- Defined Institutional Attachment Types -->
            <div class="p-3 rounded-xl bg-slate-50/80 border border-slate-200 space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500 block">
                  Select Defined Attachment Types:
                </span>
                @if (definedAttachmentTypes().length === 0) {
                  <span class="text-[10px] text-slate-400 italic">No defined attachment types found</span>
                }
              </div>
              @if (definedAttachmentTypes().length > 0) {
                <div class="flex flex-wrap gap-1.5">
                  @for (attType of definedAttachmentTypes(); track attType.id || attType.code) {
                    <button
                      type="button"
                      (click)="addDefinedAttachment(attType)"
                      [title]="attType.description || attType.name"
                      class="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-white hover:bg-emerald-50 text-slate-700 hover:text-emerald-800 border border-slate-200 hover:border-emerald-300 transition cursor-pointer shadow-2xs inline-flex items-center gap-1.5"
                    >
                      <span class="text-emerald-600 font-bold">+</span>
                      <span>{{ attType.name }}</span>
                      @if (attType.isRequired) {
                        <span class="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200 uppercase">REQ</span>
                      }
                    </button>
                  }
                </div>
              }
            </div>

            <!-- Custom Document Add Sub-Form -->
            @if (showAddAttachmentForm()) {
              <div class="p-3.5 rounded-xl bg-indigo-50/60 border border-indigo-200 space-y-3">
                <div class="text-xs font-bold text-indigo-950">Add Custom Document Requirement</div>
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label class="block text-[11px] font-bold text-slate-700 uppercase mb-1">Document Title *</label>
                    <input
                      type="text"
                      [(ngModel)]="newAttachment.name"
                      name="customAttName"
                      placeholder="e.g. ISO 27001 Security Audit"
                      class="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs text-slate-900 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label class="block text-[11px] font-bold text-slate-700 uppercase mb-1">Document Code</label>
                    <input
                      type="text"
                      [(ngModel)]="newAttachment.code"
                      name="customAttCode"
                      placeholder="e.g. DOC_ISO27001"
                      class="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs text-slate-900 font-mono focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label class="block text-[11px] font-bold text-slate-700 uppercase mb-1">Description / Instructions</label>
                    <input
                      type="text"
                      [(ngModel)]="newAttachment.description"
                      name="customAttDesc"
                      placeholder="Instructions for applicant..."
                      class="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs text-slate-900 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
                  <div>
                    <label class="block text-[11px] font-bold text-slate-700 uppercase mb-1">Mandatory Status</label>
                    <select
                      [(ngModel)]="newAttachment.isRequired"
                      name="customAttRequired"
                      class="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs text-slate-900 focus:border-indigo-500 focus:outline-none cursor-pointer"
                    >
                      <option [ngValue]="true">Mandatory (Required)</option>
                      <option [ngValue]="false">Optional (Recommended)</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-[11px] font-bold text-slate-700 uppercase mb-1">Allowed Formats</label>
                    <input
                      type="text"
                      [(ngModel)]="newAttachment.allowedExtensions"
                      name="customAttExt"
                      placeholder="pdf,docx,jpg,png"
                      class="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs text-slate-900 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label class="block text-[11px] font-bold text-slate-700 uppercase mb-1">Max File Size (MB)</label>
                    <input
                      type="number"
                      [(ngModel)]="newAttachment.maxSizeMb"
                      name="customAttSize"
                      min="1"
                      max="50"
                      class="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-xs text-slate-900 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>

                <div class="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    (click)="showAddAttachmentForm.set(false)"
                    class="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 border border-slate-200 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    (click)="addCustomAttachment()"
                    class="px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 shadow-2xs cursor-pointer"
                  >
                    Add Document
                  </button>
                </div>
              </div>
            }

            <!-- Current Attachments List -->
            @if (editForm.requiredAttachments && editForm.requiredAttachments.length > 0) {
              <div class="space-y-2 max-h-60 overflow-y-auto pr-1">
                @for (att of editForm.requiredAttachments; track $index) {
                  <div class="p-3 rounded-xl border border-slate-200 bg-white hover:border-slate-300 flex items-start justify-between gap-3 shadow-2xs">
                    <div class="space-y-1 flex-1">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="font-bold text-xs text-slate-900">{{ att.name }}</span>
                        <span class="px-1.5 py-0.2 rounded font-mono text-[10px] bg-slate-100 text-slate-600 border border-slate-200">{{ att.code }}</span>
                        <button
                          type="button"
                          (click)="att.isRequired = !att.isRequired"
                          [ngClass]="att.isRequired ? 'bg-amber-50 text-amber-800 border-amber-300' : 'bg-slate-100 text-slate-600 border-slate-200'"
                          class="px-2 py-0.5 rounded-md text-[10px] font-bold border transition cursor-pointer inline-flex items-center gap-1"
                          title="Click to toggle mandatory / optional"
                        >
                          {{ att.isRequired ? 'Mandatory' : 'Optional' }}
                        </button>
                        <span class="text-[10px] text-slate-400">Max {{ att.maxSizeMb || 10 }}MB ({{ att.allowedExtensions || 'Any' }})</span>
                      </div>
                      @if (att.description) {
                        <p class="text-[11px] text-slate-500 leading-snug">{{ att.description }}</p>
                      }
                    </div>

                    <button
                      type="button"
                      (click)="removeRequiredAttachment($index)"
                      title="Remove document requirement"
                      class="p-1 rounded-lg text-rose-500 hover:text-white hover:bg-rose-500 transition cursor-pointer"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                }
              </div>
            } @else {
              <div class="p-4 rounded-xl border border-dashed border-slate-200 text-center bg-slate-50/60 text-slate-400 text-xs">
                No attachments required yet. Requesting institutions will not be prompted to upload compliance documents unless added above.
              </div>
            }
          </div>

          <!-- Service Documentation & API Specifications: Re-engineered Architecture -->
          <div class="pt-4 border-t border-slate-200">
            <div class="p-4 rounded-2xl bg-gradient-to-br from-teal-50/90 via-slate-50 to-emerald-50/70 border border-teal-200/80 shadow-2xs space-y-2">
              <div class="flex items-start gap-3">
                <div class="p-2.5 rounded-xl bg-teal-600 text-white shrink-0 shadow-xs mt-0.5">
                  <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div class="space-y-1">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span class="text-xs font-bold text-teal-950 uppercase tracking-tight">
                      Service Documentation & API Specifications: Re-engineered Architecture
                    </span>
                    <span class="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-100 text-teal-800 border border-teal-200">
                      Provisioned Per Data Sharing Request
                    </span>
                  </div>
                  <p class="text-xs text-slate-600 leading-relaxed">
                    Technical documentation, OpenAPI specifications, and integration endpoints are now managed and uploaded directly to each <strong>Data Sharing Request</strong> after access is granted.
                  </p>
                  <p class="text-[11px] text-slate-500 leading-relaxed">
                    Because each consumer institution may have distinct API specifications, bilateral parameters, and custom endpoints, documentation is provisioned per data-sharing agreement. Consumers can preview all specifications directly within their approved request.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
            <button
              type="button"
              (click)="showEditModal.set(false)"
              class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-sm cursor-pointer"
            >
              Save Changes
            </button>
          </div>
        </form>
      </app-modal>

      <!-- Delete Confirmation Modal -->
      <app-modal
        [isOpen]="showDeleteModal()"
        title="Confirm Service Deletion"
        [errorState]="modalError()"
        (close)="showDeleteModal.set(false)"
      >
        @if (serviceToDelete(); as srv) {
          <div class="space-y-4 text-xs text-slate-700">
            <div class="p-4 rounded-2xl bg-rose-50 border border-rose-200 flex items-start gap-3">
              <div class="p-2 rounded-xl bg-rose-100 text-rose-600 shrink-0">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h4 class="font-bold text-rose-900 text-sm">Are you sure you want to delete this service?</h4>
                <p class="mt-1 text-rose-700 leading-relaxed">
                  You are about to permanently delete <strong>{{ srv.name }}</strong> (<code>{{ srv.code }}</code>) published by <strong>{{ srv.institutionName }}</strong>.
                </p>
                <p class="mt-1 text-rose-600 text-[11px]">
                  Note: Services linked to active Data Sharing Requests (DSR) cannot be deleted and must be deactivated instead.
                </p>
              </div>
            </div>

            <div class="flex justify-end gap-3 pt-3 border-t border-slate-200">
              <button
                type="button"
                (click)="showDeleteModal.set(false)"
                class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                (click)="onDeleteService()"
                class="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-500 shadow-sm cursor-pointer transition"
              >
                Delete Service
              </button>
            </div>
          </div>
        }
      </app-modal>

      <!-- Service Documentation & OpenAPI Specs Modal (Catalog Viewer) -->
      <app-modal
        [isOpen]="showServiceDocsModal()"
        [title]="'Service Documentation & API Specifications'"
        maxWidth="max-w-4xl"
        (close)="showServiceDocsModal.set(false)"
      >
        @if (viewDocsService(); as srv) {
          <div class="space-y-4 text-xs">
            <div class="p-3.5 rounded-2xl bg-teal-50/60 border border-teal-200/80 flex items-start gap-3">
              <div class="p-2 rounded-xl bg-teal-100 text-teal-800 shrink-0">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <h4 class="font-bold text-slate-900 text-sm truncate">{{ srv.name }}</h4>
                  <span class="px-2 py-0.5 rounded font-mono text-[10px] bg-white border border-teal-200 text-teal-800 font-bold">
                    {{ srv.code }}
                  </span>
                </div>
                <p class="text-slate-600 text-[11px] mt-0.5">
                  Published by <strong class="text-slate-800">{{ srv.institutionName }}</strong>. Below are technical specifications, OpenAPI schemas, and integration guidelines provided for this service.
                </p>
              </div>
            </div>

            @if (srv.documentationAttachments && srv.documentationAttachments.length > 0) {
              <div class="space-y-2.5 max-h-96 overflow-y-auto pr-1">
                @for (doc of srv.documentationAttachments; track doc.uid) {
                  <div class="p-3.5 rounded-xl border border-slate-200 bg-white hover:border-teal-300 hover:shadow-xs transition flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div class="flex items-start gap-3 min-w-0">
                      <div class="p-2 rounded-xl bg-teal-50 text-teal-700 shrink-0 mt-0.5">
                        @if (doc.docType === 'OPEN_API') {
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                          </svg>
                        } @else {
                          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        }
                      </div>
                      <div class="min-w-0">
                        <div class="flex items-center gap-2 flex-wrap">
                          <span class="font-bold text-slate-900 text-xs">{{ doc.title }}</span>
                          <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-50 text-teal-800 border border-teal-200">
                            {{ formatDocType(doc.docType) }}
                          </span>
                        </div>
                        <div class="text-[10px] text-slate-500 font-mono mt-0.5">
                          {{ doc.fileName }} &bull; {{ formatFileSize(doc.fileSize) }}
                        </div>
                      </div>
                    </div>

                    <div class="flex items-center gap-2 shrink-0 self-end sm:self-center">
                      <button
                        type="button"
                        (click)="openDocPreview(doc)"
                        class="px-3 py-1.5 rounded-lg text-xs font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 transition cursor-pointer inline-flex items-center gap-1.5 shadow-2xs"
                      >
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        <span>Preview</span>
                      </button>

                      @if (doc.fileData) {
                        <a
                          [href]="doc.fileData"
                          [download]="doc.fileName"
                          class="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-slate-800 hover:bg-slate-700 transition cursor-pointer inline-flex items-center gap-1.5 shadow-2xs"
                        >
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          <span>Download</span>
                        </a>
                      }
                    </div>
                  </div>
                }
              </div>
            } @else {
              <div class="p-6 rounded-2xl border border-dashed border-slate-200 text-center bg-slate-50/60 text-slate-400">
                <svg class="w-8 h-8 text-slate-300 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p class="font-semibold text-slate-500">No Documentation Uploaded Yet</p>
                <p class="text-[11px] text-slate-400 mt-1">The provider institution has not attached any OpenAPI specifications or technical guidelines for this service.</p>
              </div>
            }

            <div class="flex justify-end pt-3 border-t border-slate-200">
              <button
                type="button"
                (click)="showServiceDocsModal.set(false)"
                class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        }
      </app-modal>

      <!-- Interactive Document & Specification Preview Modal -->
      <app-modal
        [isOpen]="showDocPreviewModal()"
        [title]="previewDoc()?.title || 'Document Preview'"
        maxWidth="max-w-4xl"
        (close)="showDocPreviewModal.set(false)"
      >
        @if (previewDoc(); as doc) {
          <div class="space-y-4">
            <!-- Header banner with info & quick actions -->
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl bg-slate-50 border border-slate-200">
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-bold text-xs text-slate-900 truncate">{{ doc.title }}</span>
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-50 text-teal-800 border border-teal-200">
                    {{ formatDocType(doc.docType) }}
                  </span>
                </div>
                <div class="text-[10px] text-slate-500 font-mono mt-0.5">
                  {{ doc.fileName }} &bull; {{ formatFileSize(doc.fileSize) }}
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                @if (previewTextContent()) {
                  <button
                    type="button"
                    (click)="copyPreviewContent()"
                    class="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-300 transition cursor-pointer inline-flex items-center gap-1.5 shadow-2xs"
                  >
                    <svg class="w-3.5 h-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span>Copy Specification</span>
                  </button>
                }

                @if (doc.fileData) {
                  <a
                    [href]="doc.fileData"
                    [download]="doc.fileName"
                    class="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 transition cursor-pointer inline-flex items-center gap-1.5 shadow-2xs"
                  >
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span>Download</span>
                  </a>
                }
              </div>
            </div>

            <!-- Preview Body -->
            @if (previewTextContent()) {
              <div class="rounded-xl border border-slate-800 bg-slate-950 p-4 max-h-[460px] overflow-auto shadow-inner text-slate-200">
                <pre class="font-mono text-xs leading-relaxed whitespace-pre-wrap selection:bg-teal-700 selection:text-white">{{ previewTextContent() }}</pre>
              </div>
            } @else if (isPdf(doc) && pdfUrl()) {
              <div class="rounded-xl border border-slate-200 overflow-hidden bg-slate-100 shadow-inner h-[500px]">
                <iframe [src]="pdfUrl()" class="w-full h-full border-0" title="PDF Document Viewer"></iframe>
              </div>
            } @else {
              <div class="p-8 rounded-2xl border border-dashed border-slate-200 text-center bg-slate-50 space-y-3">
                <div class="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto">
                  <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h4 class="font-bold text-slate-800 text-sm">Binary or Document File</h4>
                  <p class="text-xs text-slate-500 mt-1 max-w-md mx-auto">
                    Direct in-browser text rendering is not supported for this file format (<strong>{{ doc.fileName }}</strong>). Please download the file to inspect in your local viewer or OpenAPI editor.
                  </p>
                </div>
                @if (doc.fileData) {
                  <div>
                    <a
                      [href]="doc.fileData"
                      [download]="doc.fileName"
                      class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 transition shadow-sm cursor-pointer"
                    >
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      <span>Download {{ doc.fileName }} ({{ formatFileSize(doc.fileSize) }})</span>
                    </a>
                  </div>
                }
              </div>
            }

            <div class="flex justify-end pt-3 border-t border-slate-200">
              <button
                type="button"
                (click)="showDocPreviewModal.set(false)"
                class="px-4 py-2 rounded-xl text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 cursor-pointer"
              >
                Close Preview
              </button>
            </div>
          </div>
        }
      </app-modal>
    </div>
  `
})
export class ServiceCatalogComponent implements OnInit {
  catalogService = inject(ServiceCatalogService);
  institutionService = inject(InstitutionService);
  attachmentTypeService = inject(AttachmentTypeService);
  toastService = inject(ToastService);
  authService = inject(AuthService);
  sanitizer = inject(DomSanitizer);
  router = inject(Router);

  services = signal<ServiceCatalogResponse[]>([]);
  institutions = signal<InstitutionResponse[]>([]);
  definedAttachmentTypes = signal<AttachmentTypeDto[]>([]);
  selectedSyncInstitutionUid = '';
  isSyncing = signal<boolean>(false);

  viewMode = signal<'grid' | 'table'>('table');
  statusTab = signal<'ALL' | 'MY_INSTITUTION' | 'ACTIVE' | 'PENDING'>('ALL');
  searchQuery = '';

  showEditModal = signal<boolean>(false);
  showDeleteModal = signal<boolean>(false);

  selectedService = signal<ServiceCatalogResponse | null>(null);
  serviceToDelete = signal<ServiceCatalogResponse | null>(null);

  editForm: {
    name?: string;
    code?: string;
    encodedId?: string;
    description?: string;
    active?: boolean;
    requiredAttachments: ServiceRequiredAttachmentDto[];
    documentationAttachments: ServiceDocumentationAttachmentDto[];
  } = {
    name: '',
    code: '',
    encodedId: '',
    description: '',
    active: true,
    requiredAttachments: [],
    documentationAttachments: []
  };

  showAddAttachmentForm = signal<boolean>(false);
  newAttachment: ServiceRequiredAttachmentDto = {
    name: '',
    code: '',
    description: '',
    isRequired: true,
    allowedExtensions: 'pdf,docx,jpg,png',
    maxSizeMb: 10
  };

  // Service Documentation Attachments State
  newDocAttachment: {
    title: string;
    docType: string;
  } = {
    title: '',
    docType: 'OPEN_API'
  };
  uploadingDocFile = signal<boolean>(false);

  // Service Docs Viewer & Preview Modals
  showServiceDocsModal = signal<boolean>(false);
  viewDocsService = signal<ServiceCatalogResponse | null>(null);
  showDocPreviewModal = signal<boolean>(false);
  previewDoc = signal<ServiceDocumentationAttachmentDto | null>(null);
  previewTextContent = signal<string | null>(null);
  pdfUrl = computed<SafeResourceUrl | null>(() => {
    const doc = this.previewDoc();
    if (doc && this.isPdf(doc) && doc.fileData) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(doc.fileData);
    }
    return null;
  });

  modalError = signal<ModalErrorState | null>(null);

  myServicesCount = computed(() => {
    return this.services().filter(s => this.isOwnInstitutionService(s)).length;
  });

  filteredServices = computed(() => {
    let list = this.services();

    // Hide inactive services belonging to other institutions
    if (!this.authService.isInternal() && !this.authService.hasRole('SuperAdmin')) {
      list = list.filter(s => s.active || this.isOwnInstitutionService(s));
    }

    if (this.statusTab() === 'MY_INSTITUTION') {
      list = list.filter(s => this.isOwnInstitutionService(s));
    } else if (this.statusTab() === 'ACTIVE') {
      list = list.filter(s => s.active);
    } else if (this.statusTab() === 'PENDING') {
      list = list.filter(s => !s.active);
    }

    if (this.searchQuery) {
      const q = this.searchQuery.toLowerCase();
      list = list.filter(s =>
        s.name.toLowerCase().includes(q) ||
        s.code.toLowerCase().includes(q) ||
        (s.encodedId && s.encodedId.toLowerCase().includes(q)) ||
        (s.institutionName && s.institutionName.toLowerCase().includes(q))
      );
    }
    return list;
  });

  currentPage = signal<number>(1);
  pageSize = signal<number>(10);
  totalItems = computed(() => this.filteredServices().length);

  pagedServices = computed(() => {
    const list = this.filteredServices();
    const start = (this.currentPage() - 1) * this.pageSize();
    return list.slice(start, start + this.pageSize());
  });

  onPageChange(page: number) {
    this.currentPage.set(page);
  }

  onPageSizeChange(size: number) {
    this.pageSize.set(size);
    this.currentPage.set(1);
  }

  onStatusTabChange(tab: any) {
    this.statusTab.set(tab);
    this.currentPage.set(1);
  }

  ngOnInit() {
    this.loadServices();
    this.loadDefinedAttachmentTypes();
    if (this.authService.isInternal() || this.authService.hasRole('SuperAdmin')) {
      this.institutionService.getAllInstitutions().subscribe({
        next: (res) => {
          if (res.success && res.data) {
            this.institutions.set(res.data);
          }
        }
      });
    }
  }

  loadServices() {
    this.catalogService.getAllServices().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.services.set(res.data);
        }
      },
      error: () => {
        // Fallback to getActiveServices
        this.catalogService.getActiveServices().subscribe({
          next: (res) => {
            if (res.success && res.data) this.services.set(res.data);
          }
        });
      }
    });
  }

  isOwnInstitutionService(srv: ServiceCatalogResponse): boolean {
    const myInstUid = this.authService.institutionUid();
    const myInstName = (this.authService.institutionName() || '').trim().toLowerCase();

    if (myInstUid && srv.institutionUid && String(srv.institutionUid).toLowerCase() === String(myInstUid).toLowerCase()) {
      return true;
    }
    if (myInstName && srv.institutionName && srv.institutionName.trim().toLowerCase() === myInstName) {
      return true;
    }
    return false;
  }

  canManageService(srv: ServiceCatalogResponse): boolean {
    if (this.authService.hasRole('SuperAdmin') || this.authService.isInternal()) {
      return true;
    }
    const hasPerm = this.authService.hasPermission('can manage services') ||
                    this.authService.hasRole('InstitutionAdmin') ||
                    this.authService.isInstitutionAdmin() ||
                    this.authService.hasRole('DPO') ||
                    this.authService.hasRole('DataProtectionOfficer') ||
                    this.authService.hasRole('Data Protection Officer');
    return hasPerm && this.isOwnInstitutionService(srv);
  }

  canActivate(srv: ServiceCatalogResponse): boolean {
    if (srv.active) return false;
    if (this.authService.hasRole('SuperAdmin') || this.authService.isInternal()) {
      return true;
    }
    return this.canManageService(srv);
  }

  canApprove(srv: ServiceCatalogResponse): boolean {
    return this.canActivate(srv);
  }

  async onActivateService(uid: string) {
    const srv = this.services().find(s => s.uid === uid);
    const serviceName = srv ? ` "${srv.name}"` : '';

    const confirmed = await this.toastService.confirm({
      title: 'Activate Service',
      message: `Are you sure you want to activate and publish service${serviceName} to the e-Gov Catalog? Other institutions will be able to discover and request access.`,
      confirmText: 'Yes, Activate',
      cancelText: 'Cancel',
      confirmStyle: 'success'
    });
    if (!confirmed) return;

    this.catalogService.approveService(uid).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success('Service activated and published successfully');
          this.loadServices();
        } else {
          this.toastService.error(res.message || 'Failed to activate service');
        }
      },
      error: (err) => {
        this.toastService.error(err.error?.message || err.message || 'Failed to activate service');
      }
    });
  }

  onApproveService(uid: string) {
    this.onActivateService(uid);
  }

  onSyncServices() {
    const institutionUid = (this.authService.isInternal() || this.authService.hasRole('SuperAdmin'))
      ? this.selectedSyncInstitutionUid
      : undefined;

    this.isSyncing.set(true);
    this.catalogService.syncServices(institutionUid).subscribe({
      next: (res) => {
        this.isSyncing.set(false);
        if (res.success) {
          this.toastService.success(res.message || 'Services synchronized successfully from Central Server');
          this.loadServices();
        } else {
          this.toastService.error(res.message || 'Failed to synchronize services');
        }
      },
      error: (err) => {
        this.isSyncing.set(false);
        this.toastService.error(err.error?.message || err.message || 'Failed to synchronize services from Central Server');
      }
    });
  }

  loadDefinedAttachmentTypes() {
    this.attachmentTypeService.getAllAttachmentTypes().subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.definedAttachmentTypes.set(res.data);
        }
      },
      error: () => {}
    });
  }

  openEditModal(srv: ServiceCatalogResponse) {
    if (!this.canManageService(srv)) {
      this.toastService.error('You are only authorized to edit services belonging to your institution');
      return;
    }
    this.modalError.set(null);
    this.selectedService.set(srv);
    this.showAddAttachmentForm.set(false);
    this.newDocAttachment = { title: '', docType: 'OPEN_API' };
    this.loadDefinedAttachmentTypes();
    this.editForm = {
      name: srv.name,
      code: srv.code,
      encodedId: srv.encodedId || '',
      description: srv.description || '',
      active: srv.active,
      requiredAttachments: srv.requiredAttachments ? JSON.parse(JSON.stringify(srv.requiredAttachments)) : [],
      documentationAttachments: srv.documentationAttachments ? JSON.parse(JSON.stringify(srv.documentationAttachments)) : []
    };
    this.showEditModal.set(true);
  }

  addDefinedAttachment(type: AttachmentTypeDto) {
    if (!this.editForm.requiredAttachments) {
      this.editForm.requiredAttachments = [];
    }

    const exists = this.editForm.requiredAttachments.some(
      a => a.code.toLowerCase() === type.code.toLowerCase()
    );
    if (exists) {
      this.toastService.info(`Attachment "${type.name}" is already added.`);
      return;
    }

    this.editForm.requiredAttachments.push({
      id: type.id,
      name: type.name,
      code: type.code,
      description: type.description || '',
      isRequired: type.isRequired ?? true,
      allowedExtensions: type.allowedExtensions || 'pdf,docx',
      maxSizeMb: type.maxSizeMb || 10
    });
    this.toastService.success(`Added ${type.name}`);
  }

  addCustomAttachment() {
    if (!this.newAttachment.name.trim() || !this.newAttachment.code.trim()) {
      this.toastService.error('Please enter both document name and code');
      return;
    }

    if (!this.editForm.requiredAttachments) {
      this.editForm.requiredAttachments = [];
    }

    const exists = this.editForm.requiredAttachments.some(
      a => a.code.toLowerCase() === this.newAttachment.code.trim().toLowerCase()
    );
    if (exists) {
      this.toastService.error(`Document with code "${this.newAttachment.code}" is already in the list`);
      return;
    }

    this.editForm.requiredAttachments.push({
      name: this.newAttachment.name.trim(),
      code: this.newAttachment.code.trim().toUpperCase(),
      description: this.newAttachment.description?.trim() || '',
      isRequired: this.newAttachment.isRequired,
      allowedExtensions: this.newAttachment.allowedExtensions?.trim() || 'pdf,docx',
      maxSizeMb: this.newAttachment.maxSizeMb || 10
    });

    this.newAttachment = {
      name: '',
      code: '',
      description: '',
      isRequired: true,
      allowedExtensions: 'pdf,docx,jpg,png',
      maxSizeMb: 10
    };
    this.showAddAttachmentForm.set(false);
  }

  removeRequiredAttachment(index: number) {
    this.editForm.requiredAttachments.splice(index, 1);
  }

  requestDataSharing(srv: ServiceCatalogResponse) {
    if (!srv.institutionUid || !srv.uid) {
      this.toastService.error('Missing institution or service reference');
      return;
    }
    this.router.navigate(['/dsr'], {
      queryParams: {
        requestDsr: 'true',
        producerUid: srv.institutionUid,
        serviceUid: srv.uid
      }
    });
  }

  // --- Service Documentation & OpenAPI Upload Handlers ---
  onDocFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];

    // Max 25MB
    if (file.size > 25 * 1024 * 1024) {
      this.toastService.error(`File "${file.name}" exceeds 25MB maximum limit`);
      input.value = '';
      return;
    }

    this.uploadingDocFile.set(true);
    const reader = new FileReader();
    reader.onload = () => {
      this.uploadingDocFile.set(false);
      const dataUrl = reader.result as string;
      const title = this.newDocAttachment.title.trim() || file.name;

      if (!this.editForm.documentationAttachments) {
        this.editForm.documentationAttachments = [];
      }

      this.editForm.documentationAttachments.push({
        uid: 'doc-' + Date.now(),
        title: title,
        docType: this.newDocAttachment.docType || 'OPEN_API',
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size,
        fileData: dataUrl,
        uploadedAt: new Date().toISOString()
      });

      this.toastService.success(`Attached "${file.name}" to service`);
      this.newDocAttachment.title = '';
      input.value = '';
    };
    reader.onerror = () => {
      this.uploadingDocFile.set(false);
      this.toastService.error('Failed to read file');
      input.value = '';
    };
    reader.readAsDataURL(file);
  }

  removeDocAttachment(index: number) {
    this.editForm.documentationAttachments.splice(index, 1);
  }

  openServiceDocsModal(srv: ServiceCatalogResponse) {
    this.viewDocsService.set(srv);
    this.showServiceDocsModal.set(true);

    // If documentation attachments were loaded without fileData (optimized payload), fetch full service details
    if (srv.uid && srv.documentationAttachments && srv.documentationAttachments.some(d => !d.fileData)) {
      this.catalogService.getServiceByUid(srv.uid).subscribe({
        next: (res) => {
          if (res.success && res.data) {
            this.viewDocsService.set(res.data);
          }
        }
      });
    }
  }

  openDocPreview(doc: ServiceDocumentationAttachmentDto) {
    this.previewDoc.set(doc);
    this.previewTextContent.set(null);

    if (this.isTextOrJson(doc)) {
      try {
        const base64Data = doc.fileData.split(',')[1] || doc.fileData;
        const decoded = decodeURIComponent(escape(atob(base64Data)));
        try {
          const parsed = JSON.parse(decoded);
          this.previewTextContent.set(JSON.stringify(parsed, null, 2));
        } catch {
          this.previewTextContent.set(decoded);
        }
      } catch {
        this.previewTextContent.set(null);
      }
    }

    this.showDocPreviewModal.set(true);
  }

  isTextOrJson(doc: ServiceDocumentationAttachmentDto): boolean {
    const ext = (doc.fileName || '').split('.').pop()?.toLowerCase() || '';
    const ct = (doc.contentType || '').toLowerCase();
    return ['json', 'yaml', 'yml', 'txt', 'md', 'xml', 'csv'].includes(ext)
      || ct.includes('json') || ct.includes('yaml') || ct.includes('text') || ct.includes('xml');
  }

  isPdf(doc: ServiceDocumentationAttachmentDto): boolean {
    const ext = (doc.fileName || '').split('.').pop()?.toLowerCase() || '';
    const ct = (doc.contentType || '').toLowerCase();
    return ext === 'pdf' || ct.includes('pdf');
  }

  copyPreviewContent() {
    const text = this.previewTextContent();
    if (text) {
      navigator.clipboard.writeText(text);
      this.toastService.success('Copied specification to clipboard');
    }
  }

  formatDocType(type?: string): string {
    switch (type) {
      case 'OPEN_API': return 'OpenAPI / Swagger';
      case 'GUIDELINE': return 'Guideline';
      case 'TECH_SPEC': return 'Technical Spec';
      case 'SCHEMA': return 'Payload Schema';
      default: return 'Documentation';
    }
  }

  formatFileSize(bytes?: number): string {
    if (!bytes || bytes <= 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  onUpdateService() {
    const srv = this.selectedService();
    if (!srv) return;

    this.modalError.set(null);
    const payload: UpdateServiceRequest = {
      description: this.editForm.description,
      active: this.editForm.active,
      requiredAttachments: this.editForm.requiredAttachments,
      documentationAttachments: this.editForm.documentationAttachments
    };

    this.catalogService.updateService(srv.uid, payload).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success(res.message || 'Service updated successfully');
          this.showEditModal.set(false);
          this.loadServices();
        } else if (res.success === false) {
          this.modalError.set(extractErrorDetails(res));
        }
      },
      error: (err) => {
        this.modalError.set(extractErrorDetails(err.error || err));
      }
    });
  }

  confirmDelete(srv: ServiceCatalogResponse) {
    if (!this.canManageService(srv)) {
      this.toastService.error('You are only authorized to delete services belonging to your institution');
      return;
    }
    this.modalError.set(null);
    this.serviceToDelete.set(srv);
    this.showDeleteModal.set(true);
  }

  onDeleteService() {
    const srv = this.serviceToDelete();
    if (!srv) return;

    this.modalError.set(null);
    this.catalogService.deleteService(srv.uid).subscribe({
      next: (res) => {
        if (res.success) {
          this.toastService.success(res.message || 'Service deleted successfully');
          this.showDeleteModal.set(false);
          this.loadServices();
        } else if (res.success === false) {
          this.modalError.set(extractErrorDetails(res));
        }
      },
      error: (err) => {
        this.modalError.set(extractErrorDetails(err.error || err));
      }
    });
  }
}

